/**
 * Podium d'un live : les 3 plus rapides à avoir bien répondu à une question
 * (RPC `get_live_step_podium`, migration 0655), et le top 3 final d'un défi
 * privé (classement). Règles d'affichage pures et testées.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// RPC postérieure aux types stale → client générique.
const db = supabase as unknown as SupabaseClient;

export interface PodiumEntry {
  rank: number;
  name: string;
  avatar_url: string | null;
  is_me: boolean;
  /** Temps de réponse (ms) : sur la question, ou moyen sur le défi. */
  time_ms: number | null;
  /** HORA (ou points) gagnés. */
  points: number;
}

export interface StepPodium {
  rows: PodiumEntry[];
  /** Question pas finie et pas encore répondue : rien à montrer. */
  pending: boolean;
}

/** « 3,2 s », « 12 s », « 1 min 05 ». PUR. */
export function formatAnswerTime(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 10_000) return `${(Math.round(ms / 100) / 10).toFixed(1).replace('.', ',')} s`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}`;
}

/**
 * Ordre d'affichage du podium : 2e à gauche, 1er au centre, 3e à droite.
 * Avec moins de 3 joueurs, on garde ceux qui sont là. PUR.
 */
export function podiumDisplayOrder<T extends { rank: number }>(rows: T[]): T[] {
  const top = [...rows].sort((a, b) => a.rank - b.rank).slice(0, 3);
  const [first, second, third] = top;
  return [second, first, third].filter((x): x is T => !!x);
}

export function useLiveStepPodium(challengeId: string | null, step: number | null | undefined) {
  return useQuery({
    queryKey: ['live-step-podium', challengeId ?? '', step ?? -1],
    enabled: !!challengeId && step != null && step >= 0,
    staleTime: 5_000,
    // La question peut encore recevoir des réponses : on relit une fois.
    refetchInterval: (q) => ((q.state.data as StepPodium | undefined)?.pending ? 2_000 : false),
    queryFn: async (): Promise<StepPodium> => {
      const { data, error } = await db.rpc('get_live_step_podium', {
        p_challenge: challengeId,
        p_step: step,
      });
      if (error) throw new Error(error.message);
      const d = (data ?? {}) as { rows?: PodiumEntry[]; pending?: boolean };
      return { rows: d.rows ?? [], pending: d.pending === true };
    },
  });
}
